import { Loader } from '@mantine/core'
import { Dropzone as MantineDropzone } from '@mantine/dropzone'
import { useState } from 'react'
import { Icon } from '../components/Icon'
import { Colors } from '../style/colors'

export type DropzoneState = 'idle' | 'hover' | 'loading' | 'error' | 'filled'

export interface DropzoneProps {
  onDrop: (file: File) => void
  accept?: string[]
  currentFile?: File | null
  loading?: boolean
  error?: boolean
}

interface DropzoneMessageProps {
  primaryText: string
  secondaryText: string
  iconName: 'upload' | 'refresh'
  isError?: boolean
}

function DropzoneMessage({ primaryText, secondaryText, iconName, isError }: DropzoneMessageProps) {
  const iconColor = isError ? `${Colors.Cherry}` : `${Colors.King}`
  const primaryTextColor = isError ? `${Colors.Cherry}` : `${Colors.Jet}`
  const borderColor = isError ? `${Colors.Cherry}` : `${Colors.Silver}`
  const backgroundColor = isError ? `${Colors.Mew}` : 'transparent'

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, textAlign: 'left' }}>
        <div style={{ fontWeight: 700, color: primaryTextColor }}>{primaryText}</div>
        <div style={{ color: `${Colors.Jet}` }}>{secondaryText}</div>
      </div>

      <div
        style={{
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid ${borderColor}`,
          borderRadius: 6,
          backgroundColor: backgroundColor,
        }}
      >
        <Icon name={iconName} size="lg" style={{ color: iconColor }} />
      </div>
    </div>
  )
}

function DropzoneLoading() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, textAlign: 'left' }}>
        <div style={{ fontWeight: 700 }}>Uploading ...</div>
        <div style={{ color: '#1f2937' }}>It may take a few seconds. Please wait until the upload is complete.</div>
      </div>

      <div style={{ paddingRight: 8 }}>
        <Loader size={36} />
      </div>
    </div>
  )
}

export function Dropzone({ onDrop, accept = ['text/csv'], currentFile, loading = false, error = false }: DropzoneProps) {
  const [rejectedFiles, setRejectedFiles] = useState<string[]>([])

  const handleDrop = (files: File[]) => {
    if (files.length > 0) {
      setRejectedFiles([])
      onDrop(files[0])
    }
  }

  const handleReject = (files: any[]) => {
    const errors = files.map((f) => {
      if (f.errors?.[0]?.code === 'file-invalid-type') {
        return 'Invalid file type. Please upload a CSV file'
      }
      return 'File rejected'
    })
    setRejectedFiles(errors)
  }

  const hasError = rejectedFiles.length > 0 || error

  const dropzoneState: DropzoneState = loading ? 'loading' : hasError ? 'error' : currentFile ? 'filled' : 'idle'

  const borderColor = dropzoneState === 'error' ? `${Colors.Cherry}` : `${Colors.Silver}`
  const backgroundColor = dropzoneState === 'error' ? `${Colors.Mew}` : 'transparent'

  return (
    <MantineDropzone
      onDrop={handleDrop}
      onReject={handleReject}
      accept={accept}
      multiple={false}
      disabled={loading}
      style={{
        padding: 0,
        border: 'none',
        background: 'transparent',
        cursor: dropzoneState === 'loading' ? 'not-allowed' : 'pointer',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          border: `1px ${borderColor}`,
          backgroundImage: `
          repeating-linear-gradient(to right, ${borderColor} 0, ${borderColor} 8px, transparent 8px, transparent 16px),
          repeating-linear-gradient(to left, ${borderColor} 0, ${borderColor} 8px, transparent 8px, transparent 16px),
          repeating-linear-gradient(to bottom, ${borderColor} 0, ${borderColor} 8px, transparent 8px, transparent 16px),
          repeating-linear-gradient(to top, ${borderColor} 0, ${borderColor} 8px, transparent 8px, transparent 16px)
        `,
          backgroundSize: `
          100% 1px,
          100% 1px,
          1px 100%,
          1px 100%
        `,
          backgroundPosition: `
          top,
          bottom,
          left,
          right
        `,
          backgroundRepeat: 'no-repeat',
          backgroundColor,
          borderRadius: '8px',
        }}
      >
        <MantineDropzone.Idle>
          {loading ? (
            <DropzoneLoading />
          ) : hasError && currentFile ? (
            <DropzoneMessage primaryText={currentFile.name} secondaryText="Upload failed. Drop a new file to try again." iconName="refresh" isError />
          ) : hasError ? (
            <DropzoneMessage primaryText="Invalid file format" secondaryText="Only CSV files are accepted." iconName="refresh" isError />
          ) : currentFile ? (
            <DropzoneMessage primaryText={currentFile.name} secondaryText="Drop a file or replace the current file." iconName="refresh" />
          ) : (
            <DropzoneMessage primaryText="Drop your csv file to upload" secondaryText="or click to browse it." iconName="upload" />
          )}
        </MantineDropzone.Idle>

        <MantineDropzone.Accept>
          <DropzoneMessage primaryText="Drop your csv file to upload" secondaryText="or click to browse it." iconName="upload" />
        </MantineDropzone.Accept>

        <MantineDropzone.Reject>
          <DropzoneMessage primaryText="Invalid file format" secondaryText="Only CSV files are accepted." iconName="refresh" isError />
        </MantineDropzone.Reject>
      </div>
    </MantineDropzone>
  )
}
